import { Injectable } from '@angular/core';
import { text2Kebab } from '../../shared/utils/text.utils';

export interface SetThemeVariablesOptions {
  root: HTMLElement;
  prefix?: string;
}

/**
 * BackendThemeService
 * -------------------------------------------------------------------------
 * This service is responsible for converting a nested "theme styles" JSON
 * (usually from backend config) into flattened CSS Custom Properties
 * (CSS variables) and applying them globally.
 *
 * Key features / keywords for search:
 * - css variables generator
 * - theme styles from backend
 * - dynamic css variables
 * - apply backend config to styles
 * - recursive object to css vars
 * - flatten nested theme object
 * - global :root css variables
 *
 * Usage:
 *   this.themeConfigService.setThemeVariables(apiResponse.theme_styles, { prefix: '--backend' });
 *   // Result: :root { --backend-colors-primary: #303030; ... }
 */
@Injectable({ providedIn: 'root' })
export class BackendThemeService {
  protected readonly BASE_BACKEND_STYLE_PREFIX: string = '--backend';
  private lastAppliedKeys = new Set<string>();

  setThemeVariables(
    themeVariablesJSON: object,
    rawOptions: Partial<SetThemeVariablesOptions> = {}
  ): void {
    if (themeVariablesJSON == null) {
      return;
    }

    const options: SetThemeVariablesOptions = {
      prefix: this.BASE_BACKEND_STYLE_PREFIX,
      root: document.documentElement,
      ...rawOptions
    };

    const variables: Record<string, string> = this.convertObject2StyleVariables(
      themeVariablesJSON,
      options.prefix
    );

    for (const key of this.lastAppliedKeys) {
      options.root.style.removeProperty(key);
    }

    this.lastAppliedKeys.clear();

    for (const [key, value] of Object.entries(variables)) {
      options.root.style.setProperty(key, value);
      this.lastAppliedKeys.add(key);
    }

    console.log(variables);
  }

  clearThemeVariables(root: HTMLElement = document.documentElement) {
    for (const key of this.lastAppliedKeys) {
      root.style.removeProperty(key);
    }

    this.lastAppliedKeys.clear();
  }

  private convertObject2StyleVariables(
    object: object,
    prefix: string = this.BASE_BACKEND_STYLE_PREFIX
  ): Record<string, string> {
    const result: Record<string, string> = {};

    for (const [key, value] of Object.entries(object)) {
      // Skip Invalid Values/Arrays
      if (
        (typeof value !== 'string' && typeof value !== 'object') ||
        (typeof value === 'object' && Array.isArray(value))
      ) {
        continue;
      }

      const keyPrefix: string = `${prefix}-${text2Kebab(key)}`;
      if (typeof value === 'string') {
        result[keyPrefix] = value;
      }

      if (typeof value === 'object') {
        const nestedVars: Record<string, string> =
          this.convertObject2StyleVariables(
            value as Record<string, string | object>,
            keyPrefix
          );

        Object.assign(result, nestedVars);
      }
    }

    return result;
  }
}
